// CarOS - Wiederverwendbare Foto-Hinzufügen-UI (Mobile-Workflow)
// Zwei klar getrennte Buttons:
//  - "Foto aufnehmen"        -> capture="environment" (mobile Rückkamera)
//  - "Aus Galerie auswählen" -> OHNE capture, Mehrfachauswahl, inkl. HEIC
import React, { useRef } from "react";
import { AlertCircle, Camera, CheckCircle2, Images, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export const PhotoAddButtons = ({ onFiles, disabled = false, testPrefix = "photo-add", hint = true }) => {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const handle = (e) => {
    if (e.target.files?.length) onFiles(e.target.files);
    e.target.value = ""; // gleiche Datei erneut wählbar
  };
  return (
    <div className="space-y-2">
      {/* Kamera: erzwingt mobile Kamera (capture) - NUR hier */}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={handle} data-testid={`${testPrefix}-camera-input`} />
      {/* Galerie: bewusst OHNE capture-Attribut + Mehrfachauswahl + HEIC */}
      <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,image/*" multiple className="hidden"
        onChange={handle} data-testid={`${testPrefix}-gallery-input`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button type="button" variant="secondary" className="h-12 gap-2 font-semibold" disabled={disabled}
          onClick={() => camRef.current?.click()} data-testid={`${testPrefix}-camera-button`}>
          <Camera className="h-4 w-4" /> Foto aufnehmen
        </Button>
        <Button type="button" variant="outline" className="h-12 gap-2 font-semibold" disabled={disabled}
          onClick={() => galRef.current?.click()} data-testid={`${testPrefix}-gallery-button`}>
          <Images className="h-4 w-4" /> Aus Galerie auswählen
        </Button>
      </div>
      {hint && (
        <p className="text-[11px] text-muted-foreground">
          Du kannst neue Fotos aufnehmen oder bereits vorhandene Bilder verwenden.
          JPG, PNG, WEBP &amp; HEIC (iPhone) – mehrere Bilder gleichzeitig möglich.
        </p>
      )}
    </div>
  );
};

const STATUS_TEXT = {
  wartet: "Wartet...",
  vorbereiten: "Wird vorbereitet...",
  fertig: "Hochgeladen",
};

export const UploadQueue = ({ items, onRetry, onDismiss, testPrefix = "photo-queue" }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid={testPrefix}>
      {items.map((i) => (
        <div key={i.qid}
          className={`rounded-lg border px-2.5 py-1.5 text-xs ${i.status === "fehler" ? "border-red-500/40 bg-red-500/10" : "border-border bg-secondary/30"}`}
          data-testid={`${testPrefix}-item-${i.status}`}>
          <div className="flex items-center gap-2">
            {i.status === "fertig" ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
            ) : i.status === "fehler" ? (
              <AlertCircle className="h-3.5 w-3.5 text-red-300 shrink-0" />
            ) : (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
            )}
            <span className="truncate flex-1">{i.name}</span>
            <span className="text-muted-foreground shrink-0 tabular-nums">
              {i.status === "hochladen" ? `${i.progress}%` : STATUS_TEXT[i.status] || ""}
            </span>
            {i.status === "fehler" && (
              <span className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                  onClick={() => onRetry(i.qid)} data-testid={`${testPrefix}-retry-button`}>
                  <RotateCcw className="h-3 w-3" /> Erneut
                </Button>
                <button type="button" className="p-0.5 text-muted-foreground hover:text-foreground"
                  onClick={() => onDismiss(i.qid)} data-testid={`${testPrefix}-dismiss-button`} aria-label="Eintrag entfernen">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
          {(i.status === "hochladen" || i.status === "vorbereiten" || i.status === "wartet") && (
            <Progress value={i.progress} className="h-1 mt-1.5" />
          )}
          {i.status === "fehler" && <p className="text-[10px] text-red-300 mt-1">{i.error}</p>}
        </div>
      ))}
    </div>
  );
};
